import { Link } from 'gatsby';
import React, { useState } from 'react';
import { ArrowRight, CalendarCheck, FilePlus, Presentation } from 'lucide-react';
import { Headline } from '../Headline';
import { Kicker } from '../Kicker';
import { Reveal } from '../motion';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const INK = '#0e1c2b';

const Section = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const SectionHeader = styled('div')`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 32px;
`;

const SectionDescription = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 16px 0 0;
`;

const TabBar = styled('div')`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 32px;
`;

const Tab = styled('button')<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.active ? COLORS.PRIMARY : '#64748b')};
  background: ${(p) => (p.active ? '#e6f7ff' : '#fff')};
  border: 1px solid ${(p) => (p.active ? '#bae7ff' : '#e2e8f0')};
  border-radius: 100px;
  padding: 9px 18px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
  }
`;

const WorkflowPanel = styled('div')`
  display: grid;
`;

const WorkflowRow = styled('div')<{ $active?: boolean }>`
  grid-area: 1 / 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: start;
  visibility: ${(p) => (p.$active ? 'visible' : 'hidden')};
  pointer-events: ${(p) => (p.$active ? 'auto' : 'none')};

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const WorkflowContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
`;

const WorkflowKickerRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const WorkflowIconBadge = styled('div')`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkflowKicker = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
`;

const WorkflowBody = styled('p')`
  font-size: 16px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
  max-width: 480px;
`;

const LearnMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`;

const ChatWindow = styled('div')`
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 40px rgba(14, 28, 43, 0.08);
`;

const ChatHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
`;

const ClientAvatar = styled('div')`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClientLogo = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ChatHeaderName = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
`;

const ChatHeaderStatus = styled('div')`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
    box-shadow: 0 0 6px rgba(82, 196, 26, 0.7);
  }
`;

const ChatBody = styled('div')`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const UserBubbleRow = styled('div')`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled('div')`
  max-width: 88%;
  background: ${COLORS.PRIMARY};
  color: #fff;
  padding: 10px 14px;
  border-radius: 16px 16px 4px 16px;
  font-size: 14px;
  line-height: 1.5;
`;

const AssistantBubbleRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const AssistantBubble = styled('div')`
  flex: 1;
  min-width: 0;
  color: ${INK};
  font-size: 14px;
  line-height: 1.65;
`;

const ResponseList = styled('ul')`
  margin: 10px 0 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResponseListItem = styled('li')`
  color: #334155;

  strong {
    font-weight: 600;
    color: ${INK};
  }
`;

const StatusText = styled('span')<{ tone?: 'green' | 'amber' | 'red' }>`
  font-weight: 600;
  color: ${(p) =>
    p.tone === 'green' ? '#166534' : p.tone === 'amber' ? '#92400e' : '#991b1b'};
`;

const ResponseNote = styled('p')`
  margin: 12px 0 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
`;

const AttachmentCard = styled('div')`
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
`;

const AttachmentHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #f1f5f9;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const SlideMockBody = styled('div')`
  padding: 16px;
`;

const SlideMockTitle = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
  margin-bottom: 12px;
`;

const SlideMockBars = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 56px;
  padding: 0 2px;
`;

const SlideMockBar = styled('div')`
  flex: 1;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(
    180deg,
    ${COLORS.PRIMARY_LIGHT} 0%,
    ${COLORS.PRIMARY} 100%
  );
`;

const SlideMockCaption = styled('div')`
  margin-top: 10px;
  font-size: 11.5px;
  color: #94a3b8;
`;

const DraftCard = styled('div')`
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
`;

const DraftCardHeader = styled('div')`
  padding: 9px 12px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 700;
  color: ${INK};
  border-bottom: 1px solid #e2e8f0;
`;

const DraftCardBody = styled('div')`
  padding: 10px 12px 12px;
`;

const DraftRow = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const DraftLabel = styled('span')`
  color: #64748b;
  flex-shrink: 0;
`;

const DraftValue = styled('span')`
  color: ${INK};
  font-weight: 600;
  text-align: right;
`;

const ReportChip = styled('span')`
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1d4ed8;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

type Workflow = {
  id: string;
  tabLabel: string;
  icon: React.ReactNode;
  kicker: string;
  title: string;
  body: string;
  client: { name: string; logo: string };
  prompt: string;
  response: string;
  statusRows?: {
    name: string;
    metric: string;
    status: string;
    tone: 'green' | 'amber' | 'red';
  }[];
  responseNote?: string;
  draftCard?: { title: string; rows: { label: string; value: string }[] };
  attachment?: { fileName: string; title: string; caption: string };
  reportLabel: string;
};

const workflows: Workflow[] = [
  {
    id: 'summaries',
    tabLabel: 'Daily campaign summaries',
    icon: <CalendarCheck size={16} />,
    kicker: 'Protect delivery',
    title: 'Get a daily read on every in-flight campaign',
    body:
      'Most teams learn a campaign is behind when the make-good conversation starts. Ask for a daily status summary and see which deals are on target, which need attention, and where delivery risk is building, without opening another spreadsheet.',
    prompt: 'Which campaigns are behind pace this week, and by how much?',
    response: 'You have two campaigns behind pace this week:',
    statusRows: [
      {
        name: 'Bose Spring Audio',
        metric: '61% of goal',
        status: '12 days left in the flight',
        tone: 'amber'
      },
      {
        name: 'Wayfair Home Refresh',
        metric: '38% of goal',
        status: '6 days left in the flight',
        tone: 'red'
      }
    ],
    responseNote:
      'Fujifilm Promo Q1–Q2 is on track at 93% of goal. Wayfair Home Refresh is the highest delivery risk.',
    reportLabel: 'Open the campaign report',
    client: { name: 'Copilot', logo: '/images/logos/copilot-mcp.svg' }
  },
  {
    id: 'wrap',
    tabLabel: 'Wrap reports',
    icon: <Presentation size={16} />,
    kicker: 'Report and renew',
    title: 'Build wrap reports without piecing together SubIDs',
    body:
      'Campaign, placement, and page performance live in the same system as your commissions. Ask for the wrap-up and get placement-level results ready for the client deck, with a link to the live report behind the numbers.',
    prompt: 'Prepare a wrap report for the Fujifilm Promo campaign.',
    response:
      'Your Fujifilm Promo campaign delivered $30.9K in sales volume across 15 pages, with 142 clicks and a 35.2% conversion rate. Top placement: /best-mirrorless-cameras.',
    attachment: {
      fileName: 'Fujifilm_Promo_Wrap.pptx',
      title: 'Fujifilm Promo Wrap',
      caption: 'Sales volume by week'
    },
    reportLabel: 'Open the full campaign report',
    client: { name: 'Claude', logo: '/images/logos/claude-mcp.svg' }
  },
  {
    id: 'draft',
    tabLabel: 'Sponsorship campaign drafts',
    icon: <FilePlus size={16} />,
    kicker: 'Pitch and launch',
    title: 'Turn a brand brief into a scoped campaign draft',
    body:
      'Describe the sponsored content deal you want to sell and get a draft scoped to your pages, flight dates, and delivery goals. Your team reviews everything before launch, so the agent speeds up the work without skipping approval.',
    prompt:
      'Draft a sponsored content campaign for Fujifilm on our mirrorless camera guides. Q4 flight, $25K flat spend goal.',
    response:
      'I scoped a sponsorship draft from your page history and the brand brief:',
    draftCard: {
      title: 'Fujifilm Mirrorless Q4',
      rows: [
        { label: 'Flight', value: 'Oct 1 - Dec 31, 2026' },
        { label: 'Spend goal', value: '$25,000 flat' },
        { label: 'Placements', value: '8 camera buying guides' },
        { label: 'Tracking', value: 'Campaign links on included URLs' }
      ]
    },
    responseNote:
      'Review the draft, adjust placements or goals, and launch when your team is ready.',
    reportLabel: 'Review the campaign draft',
    client: { name: 'Claude', logo: '/images/logos/claude-mcp.svg' }
  }
];

export const AmplifyMcpSection = () => {
  const [activeId, setActiveId] = useState(workflows[0].id);

  return (
    <Section>
      <SectionHeader>
        <Reveal>
          <Kicker>MCP · Early access</Kicker>
          <Headline component="h2" style={{ margin: 0 }}>
            Act on campaigns from the AI tools you already use
          </Headline>
          <SectionDescription>
            Affilimate MCP brings your campaign data into Claude, ChatGPT,
            Cursor, and the rest of your stack. Ask for delivery status,
            draft sponsorship campaigns, and build wrap reports without
            rebuilding anything by hand.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      <Reveal delay={80}>
        <TabBar role="tablist" aria-label="MCP campaign workflows">
          {workflows.map((w) => (
            <Tab
              key={w.id}
              type="button"
              role="tab"
              aria-selected={activeId === w.id}
              active={activeId === w.id}
              onClick={() => setActiveId(w.id)}
            >
              {w.tabLabel}
            </Tab>
          ))}
        </TabBar>
      </Reveal>

      <WorkflowPanel>
        {workflows.map((workflow) => (
          <WorkflowRow
            key={workflow.id}
            role="tabpanel"
            aria-hidden={activeId !== workflow.id}
            $active={activeId === workflow.id}
          >
            <WorkflowContent>
              <WorkflowKickerRow>
                <WorkflowIconBadge>{workflow.icon}</WorkflowIconBadge>
                <WorkflowKicker>{workflow.kicker}</WorkflowKicker>
              </WorkflowKickerRow>
              <Headline component="h3" variant="h4" style={{ margin: 0 }}>
                {workflow.title}
              </Headline>
              <WorkflowBody>{workflow.body}</WorkflowBody>
              <LearnMoreLink to="/mcp/">
                Learn about Affilimate MCP <ArrowRight size={16} />
              </LearnMoreLink>
            </WorkflowContent>
            <ChatWindow aria-hidden>
              <ChatHeader>
                <ClientAvatar>
                  <ClientLogo src={workflow.client.logo} alt="" />
                </ClientAvatar>
                <ChatHeaderName>{workflow.client.name}</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                <UserBubbleRow>
                  <UserBubble>{workflow.prompt}</UserBubble>
                </UserBubbleRow>
                <AssistantBubbleRow>
                  <ClientAvatar>
                    <ClientLogo src={workflow.client.logo} alt="" />
                  </ClientAvatar>
                  <AssistantBubble>
                    {workflow.response}
                    {workflow.statusRows && (
                      <ResponseList>
                        {workflow.statusRows.map((row) => (
                          <ResponseListItem key={row.name}>
                            <strong>{row.name}</strong>: {row.metric},{' '}
                            <StatusText tone={row.tone}>{row.status}</StatusText>
                          </ResponseListItem>
                        ))}
                      </ResponseList>
                    )}
                    {workflow.draftCard && (
                      <DraftCard>
                        <DraftCardHeader>
                          {workflow.draftCard.title}
                        </DraftCardHeader>
                        <DraftCardBody>
                          {workflow.draftCard.rows.map((row) => (
                            <DraftRow key={row.label}>
                              <DraftLabel>{row.label}</DraftLabel>
                              <DraftValue>{row.value}</DraftValue>
                            </DraftRow>
                          ))}
                        </DraftCardBody>
                      </DraftCard>
                    )}
                    {workflow.responseNote && (
                      <ResponseNote>{workflow.responseNote}</ResponseNote>
                    )}
                    {workflow.attachment && (
                      <AttachmentCard>
                        <AttachmentHeader>
                          <Presentation size={13} />
                          {workflow.attachment.fileName}
                        </AttachmentHeader>
                        <SlideMockBody>
                          <SlideMockTitle>
                            {workflow.attachment.title}
                          </SlideMockTitle>
                          <SlideMockBars>
                            <SlideMockBar style={{ height: '45%' }} />
                            <SlideMockBar style={{ height: '70%' }} />
                            <SlideMockBar style={{ height: '90%' }} />
                            <SlideMockBar style={{ height: '60%' }} />
                            <SlideMockBar style={{ height: '80%' }} />
                          </SlideMockBars>
                          <SlideMockCaption>
                            {workflow.attachment.caption}
                          </SlideMockCaption>
                        </SlideMockBody>
                      </AttachmentCard>
                    )}
                    <ReportChip>{workflow.reportLabel}</ReportChip>
                  </AssistantBubble>
                </AssistantBubbleRow>
              </ChatBody>
            </ChatWindow>
          </WorkflowRow>
        ))}
      </WorkflowPanel>
    </Section>
  );
};
